import React, { useEffect } from 'react';
import DocumentTitle from 'react-document-title';
import { useTranslation } from 'react-i18next';
import Routes from 'utils/routes.js.erb';
import { authHeaders } from 'actions';
import { errorMessage, customMessage, clearMessage } from 'actions/internal';
import i18n from 'utils/i18n';
import TopNavBar from 'containers/nav/TopNavBar';
import SubNavBar from 'containers/nav/SubNavBar';
import AdministrationSubNav from 'components/administration/AdministrationSubNav';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { appNamedRoutes } from 'routes';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import axios from 'axios'
import { LoadingSpinner } from 'components/helpers/LoadingSpinner';
import AdminCard from 'components/administration/staff/AdminCard';
import ActionsTable from 'components/administration/staff/ActionTable';
import OrganizationAccess from 'components/administration/staff/OrganizationAccess';
import { useForm, FormProvider } from 'react-hook-form';

const AdministrationStaffEditPage = ({
  auth,
  onShowLoading,
  onHideLoading,
  onErrorMessage,
  onCustomMessage,
  match,
  history,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient()
  const { id: adminId } = match.params;
  const queryKey = ['administration', 'admin-users', adminId]
  const formMethods = useForm()

  const adminQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => axios.get(
      Routes.api_dashboard_administration_admin_user_path({
        id: adminId,
      }),
      { headers: authHeaders(), signal }
    ).then(response => response.data),
  })

  const delGroup = useMutation({
    mutationFn: (groupId) => {
      return axios.delete(Routes.group_destroy_api_dashboard_administration_admin_user_path(
        adminId, groupId
      ), { headers: authHeaders() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
    },
    onError: (error) => {
      onErrorMessage(error.response.data.message);
    },
  })

  const delSubgroup = useMutation({
    mutationFn: (subgroupId) => {
      return axios.delete(Routes.subgroup_destroy_api_dashboard_administration_admin_user_path(
        adminId, subgroupId
      ), { headers: authHeaders() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
    },
    onError: (error) => {
      onErrorMessage(error.response.data.message);
    },
  })

  const delCohort = useMutation({
    mutationFn: (cohortId) => {
      return axios.delete(Routes.cohort_destroy_api_dashboard_administration_admin_user_path(
        adminId, cohortId
      ), { headers: authHeaders() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
    },
    onError: (error) => {
      onErrorMessage(error.response.data.message);
    },
  })

  const addGroup = useMutation({
    mutationFn: (groupId) => {
      return axios.post(Routes.group_create_api_dashboard_administration_admin_user_path(
        adminId
      ), { group_id: groupId }, { headers: authHeaders() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
    },
    onError: (error) => {
      onErrorMessage(error.response.data.message);
    },
  })

  const addSubgroup = useMutation({
    mutationFn: (subgroupId) => {
      return axios.post(Routes.subgroup_create_api_dashboard_administration_admin_user_path(
        adminId
      ), { subgroup_id: subgroupId }, { headers: authHeaders() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
    },
    onError: (error) => {
      onErrorMessage(error.response.data.message);
    },
  })

  const addCohort = useMutation({
    mutationFn: (cohortId) => {
      return axios.post(Routes.cohort_create_api_dashboard_administration_admin_user_path(
        adminId
      ), {cohort_id: cohortId}, { headers: authHeaders() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
    },
    onError: (error) => {
      onErrorMessage(error.response.data.message);
    },
  })


  const resetPassword = useMutation({
    mutationFn: () => {
      return axios.put(Routes.reset_password_api_dashboard_administration_admin_user_path(
        adminId
      ), {}, { headers: authHeaders() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
      onCustomMessage(t('administration.staff.passwordInstructionsSent'), 'success');
    },
    onError: (error) => {
      if (error.response.status === 500) {
        onErrorMessage(t('errors.generic'));
        return
      }

      onErrorMessage(error.response.data.message);
    },
  })

  const archiveToggle = useMutation({
    mutationFn: () => {
      return axios.put(Routes.archive_toggle_api_dashboard_administration_admin_user_path(
        adminId
      ), {}, { headers: authHeaders() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
      if (adminQuery.data.archived) {
        onCustomMessage(t('administration.staff.unarchive'), 'success');
      } else {
        onCustomMessage(t('administration.staff.archive'), 'success');
      }
    },
    onError: (error) => {
      if (error.response.status === 500) {
        onErrorMessage(t('errors.generic'));
        return
      }

      onErrorMessage(error.response.data.message);
    },
  })

  const updateAdmin = useMutation({
    mutationkey: ['administration', 'admin-users', 'save'],
    mutationFn: (admin) => {
      return axios.put(Routes.api_dashboard_administration_admin_user_path(
        adminId
      ), { admin_user: admin }, { headers: authHeaders() })
    },
    onSuccess: () => {
      onCustomMessage(t('administration.staff.adminCreated'), 'success');
      queryClient.invalidateQueries({ queryKey: ['administration', 'admin-users'] }).then(() => {
        history.push(`${appNamedRoutes.administrationStaff}`);
      })
    },
    onError: (error) => {
      if (error.response.status === 500) {
        onErrorMessage(t('errors.generic'));
        return
      }

      if (error.response.status === 422) {
        onErrorMessage(t('errors.formValidation'));

        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          if (key === 'base') {
            onErrorMessage(errors[key].join(', '));
            return
          }

          formMethods.setError(key, {
            type: 'manual',
            message: errors[key].join(', '),
          });
        });

        return
      }
    },
  })

  useEffect(() => {
    if (adminQuery.isError) {
      if (adminQuery.error.response && adminQuery.error.response.status === 404) {
        onErrorMessage(t('errors.generic'));
        const queryParams = history.location.search;
        history.push(`${appNamedRoutes.administrationStaff}${queryParams}`)
      }
    }

    if (adminQuery.isPending) {
      onShowLoading();
    } else {
      onHideLoading();

      formMethods.reset({
        name: adminQuery.data?.name,
        email: adminQuery.data?.email,
        using_saml: adminQuery.data?.using_saml,
        partner_custom_tag_association: adminQuery.data?.partner_custom_tag_association,
      });
    }
  }, [
    history.push,
    formMethods.reset,
    adminQuery.isPending,
    onErrorMessage,
    onHideLoading,
    onShowLoading,
    adminQuery.isError,
    adminQuery.error?.response,
    adminQuery.data?.name,
    adminQuery.data?.email,
    adminQuery.data?.is_administrator,
    adminQuery.data?.partner_custom_tag_association,
    history,
    formMethods,
  ]);

  if (!adminQuery.data) {
    return (<DocumentTitle title={t('administration.pageTitle')}>
      <div>
        <TopNavBar />
        <SubNavBar />
        <AdministrationSubNav />
        <div className="row content">
          <div className="column">
            <LoadingSpinner bgColor='light' />
          </div>
        </div>
      </div>
    </DocumentTitle>
    );
  }

  return (<DocumentTitle title={t('administration.pageTitle')}>
    <div>
      <TopNavBar />
      <SubNavBar />
      <AdministrationSubNav />
      <div className="row content">
        <div className="column">
          <div className='u-margin-top--base u-margin-bottom--large'>
            <span className='o-sp-button o-sp-button--back' onClick={() =>{
              const queryParams = history.location.search;
              history.push(`${appNamedRoutes.administrationStaff}${queryParams}`)
            }}>
              <i className='sp-icon-arrow-left'></i>&nbsp;{t('administration.staff.back')}
            </span>
          </div>
          <div className="row">
            <div className="column small-12 medium-6">
              <FormProvider {...formMethods}>
                <AdminCard
                  auth={auth}
                  admin={adminQuery.data}
                  onResetPassword={() => {
                    window.confirm(i18n.t('confirm.sendPasswordReset')) &&
                    resetPassword.mutate(adminId)
                  }}
                  onArchive={() => {
                    if (adminQuery.data.archived) {
                      return window.confirm(i18n.t('confirm.unarchiveAdmin')) && archiveToggle.mutate(adminId)
                    } else {
                      return window.confirm(i18n.t('confirm.archiveAdmin')) && archiveToggle.mutate(adminId)
                    }
                  }}
                  onUpdate={(admin) => {
                    // rename the key partner_custom_tag_association to partner_custom_tag_association_attributes
                    admin.partner_custom_tag_association_attributes = admin.partner_custom_tag_association
                    delete admin.partner_custom_tag_association

                    // if the partner_custom_tag_association_attributes._destroy is true, then we need to delete the partner_custom_tag_association
                    if (admin.partner_custom_tag_association_attributes?._destroy) {
                      admin.partner_custom_tag_association_attributes = {
                        ...adminQuery.data.partner_custom_tag_association,
                        _destroy: true,
                      }
                    }

                    updateAdmin.mutate(admin)
                  }}
                />
              </FormProvider>
              <ActionsTable actions={adminQuery.data.partner_actions} />
            </div>
            <div className="column small-12 medium-6">
              <OrganizationAccess
                auth={auth}
                organization_access={adminQuery.data.organization_access}
                groups={adminQuery.data.groups}
                subgroups={adminQuery.data.subgroups}
                cohorts={adminQuery.data.cohorts}
                delGroup={(id) => {
                  window.confirm(i18n.t('confirm.deleteGroup')) &&
                  delGroup.mutate(id)
                }}
                delSubgroup={(id) => {
                  window.confirm(i18n.t('confirm.deleteSubGroup')) &&
                  delSubgroup.mutate(id)
                }}
                delCohort={(id) => {
                  window.confirm(i18n.t('confirm.deleteCohort')) &&
                  delCohort.mutate(id)
                }}
                addGroup={(id) => {
                  addGroup.mutate(id)
                }}
                addSubgroup={(id) => {
                  addSubgroup.mutate(id)
                }}
                addCohort={(id) => {
                  addCohort.mutate(id)
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </DocumentTitle>
  );
}

AdministrationStaffEditPage.propTypes = {
  auth: PropTypes.object.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onHideLoading: PropTypes.func.isRequired,
  onErrorMessage: PropTypes.func.isRequired,
  onCustomMessage: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
}

const mapDispatchToProps = (dispatch) => {
  return {
    onErrorMessage: (message) => {
      dispatch(errorMessage(message))
    },
    onCustomMessage: (message, messageType) => {
      dispatch(customMessage(message, messageType))
    },
    onClearMessage: () => {
      dispatch(clearMessage())
    },
    onShowLoading: () => {
      dispatch(showLoading())
    },
    onHideLoading: () => {
      dispatch(hideLoading())
    },
  }
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AdministrationStaffEditPage);
